import { InputProps, InputSizes } from './input';

import { css, cx } from 'linaria';
import { BoxOwnProps } from '../box';
import { Variables } from '../index';

type InputBoxProps = Pick<
    BoxOwnProps,
    | 'fontSize'
    | 'padding'
    | 'height'
    | 'borderRadius'
    | 'borderColor'
    | 'display'
    | 'alignItems'
    | 'position'
    | 'transitionDuration'
    | 'transitionProperty'
    | 'transitionTimingFunction'
    | 'outline'
    | 'appearance'
    | 'border'
    | 'boxShadow'
    | 'width'
    | 'color'
>;

const baseProps: InputBoxProps = {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    transitionDuration: 'fast',
    transitionProperty: 'all',
    transitionTimingFunction: 'standard',
    outline: 'none',
    appearance: 'none',
    padding: { top: '0', left: '0', right: '0', bottom: '0' },
};

export const theme = css`
    :global() {
        :root {
            [data-theme='light'] {
                --input-background-color: ${Variables.css.catapultWhite};
                --input-resting-text-color: ${Variables.css.grey3};
                --input-resting-border-color: ${Variables.css.grey3};
                --input-hover-text-color: ${Variables.css.grey3};
                --input-hover-border-color: ${Variables.css.catapultBlack};
                --input-filled-text-color: ${Variables.css.catapultBlack};
                --input-filled-border-color: ${Variables.css.catapultBlack};
                --input-error-text-color: ${Variables.css.catapultBlack};
                --input-error-border-color: ${Variables.css.alert};
            }

            [data-theme='dark'] {
                --input-background-color: ${Variables.css.catapultBlack};
                --input-resting-text-color: ${Variables.css.grey4};
                --input-resting-border-color: ${Variables.css.grey5};
                --input-hover-text-color: ${Variables.css.grey5};
                --input-hover-border-color: ${Variables.css.catapultWhite};
                --input-filled-text-color: ${Variables.css.catapultWhite};
                --input-filled-border-color: ${Variables.css.catapultWhite};
                --input-error-text-color: ${Variables.css.catapultWhite};
                --input-error-border-color: ${Variables.css.alert};
            }
        }
    }
`;
const errorClassName = css``;

const primaryStateStyles = css`
    background-color: var(--input-background-color);
    color: var(--input-filled-text-color);
    border: solid 1px var(--input-filled-border-color);
    &::placeholder {
        color: var(--input-resting-text-color);
    }
    &:placeholder-shown {
        border-color: var(--input-resting-border-color);
    }
    &:hover {
        border-color: var(--input-hover-border-color);
        box-shadow: 0 0 0 1px var(--input-hover-border-color);
    }
    &:focus-visible,
    &.${errorClassName}:focus-visible {
        outline: none;
        border-color: var(--r-information);
        box-shadow: 0 0 0 2px var(--r-information);
    }
    &:read-only {
        box-shadow: none;
        user-select: all;
        border-color: var(--input-resting-border-color);
    }
    &:disabled {
        cursor: not-allowed;
    }

    &.${errorClassName} {
        border-color: var(--r-alert);
        box-shadow: 0 0 0 1px var(--r-alert);
    }
`;

const resizeNone = css`
    resize: none;
`;

export const inputSizes: Record<InputSizes, InputBoxProps> = {
    small: {
        fontSize: '14',
        padding: { left: '4', right: '4' },
        height: '28',
        borderRadius: '4',
    },
    base: {
        fontSize: '14',
        padding: { left: '12', right: '12' },
        height: '44',
        borderRadius: '4',
    },
    medium: {
        fontSize: '14',
        padding: { left: '12', right: '12' },
        height: '44',
        borderRadius: '4',
    },
};

const useInputStyles = ({ size, fullWidth, error, noResize, as }: InputProps): BoxOwnProps => {
    const sizeProps = inputSizes[size];
    const className = cx(noResize && resizeNone, error && errorClassName, primaryStateStyles);
    const styles: BoxOwnProps = {
        width: fullWidth ? 'full' : undefined,
        ...baseProps,
        ...sizeProps,
        className,
    };

    if (as === 'textarea') {
        styles.padding = { ...styles.padding, top: styles.padding.left };
    }

    return styles;
};

export default useInputStyles;
